import { type ChangeEvent, useCallback, useEffect, useState } from "react";

import { PageNavigation } from "~/components/PageNavigation";
import { getLicenseExtensionPurchases } from "~/modules/sales/licenseExtensions/services";
import type { LicenseExtensionPurchase } from "~/modules/sales/licenseExtensions/types";
import { numberToBRL } from "~/modules/sales/services";
import { debouncer } from "~/utils/debounce";
import { getLocalDate } from "~/utils/getLocalDate";

import { useLicenses } from "../../Licenses/context";

type FilterQuery = {
	page: number;
	size: number;
	licenseExtensionIds: string[];
	name: string;
};

const debounce = debouncer();

export const LicenseExtensionPurchases: React.FC = () => {
	const { extensions } = useLicenses();
	const [purchases, setPurchases] = useState<LicenseExtensionPurchase[]>([]);
	const [totalItems, setTotalItems] = useState(0);
	const [filterQuery, setFilterQuery] = useState<FilterQuery>({
		page: 0,
		size: 10,
		licenseExtensionIds: [],
		name: "",
	});

	useEffect(() => {
		const controller = new AbortController();
		debounce(() => {
			getLicenseExtensionPurchases(filterQuery, controller.signal).then(
				(data) => {
					if (!data) return;
					setPurchases(data.data);
					setTotalItems(data.paging.total);
				},
			);
		});
		return () => {
			controller.abort();
		};
	}, [filterQuery]);

	const handleUpdatePage = useCallback((page: number) => {
		setFilterQuery((s) => ({ ...s, page }));
	}, []);

	const handleNameFilter = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		debounce(() =>
			setFilterQuery((s) => ({ ...s, page: 0, name: e.target.value })),
		);
	}, []);

	const toggleExtensionFilter = useCallback((id: string) => {
		setFilterQuery((s) => {
			const already = s.licenseExtensionIds.includes(id);
			return {
				...s,
				page: 0,
				licenseExtensionIds: already
					? s.licenseExtensionIds.filter((x) => x !== id)
					: [...s.licenseExtensionIds, id],
			};
		});
	}, []);

	return (
		<div className="flex flex-col flex-1 gap-4 m-5 p-5 bg-white rounded-xl overflow-hidden">
			<div className="flex flex-col gap-2">
				<p>Filtrar por:</p>
				<div className="flex gap-3 flex-wrap items-center">
					<input
						type="text"
						placeholder="Busca por personal trainer"
						className="border-2 outline-none focus-within:ring-2
              ring-gpa-blue-400 duration-150 px-2 rounded-md"
						onChange={handleNameFilter}
					/>
					<span className="text-slate-400 text-sm">Pacote:</span>
					{extensions.map((ext) => (
						<button
							key={ext.id}
							data-active={filterQuery.licenseExtensionIds.includes(ext.id)}
							onClick={() => toggleExtensionFilter(ext.id)}
							className="px-3 py-1 text-sm rounded-md text-gpa-blue-500
                data-[active=true]:bg-gpa-blue-500 data-[active=true]:text-white
                hover:bg-gpa-blue-50 bg-slate-50"
						>
							{ext.name}
						</button>
					))}
					{filterQuery.licenseExtensionIds.length > 0 && (
						<button
							className="px-3 py-1 text-sm rounded-md text-slate-500 bg-slate-100 hover:bg-slate-200"
							onClick={() =>
								setFilterQuery((s) => ({
									...s,
									licenseExtensionIds: [],
									page: 0,
								}))
							}
						>
							Limpar
						</button>
					)}
				</div>
			</div>

			<div className="max-w-full flex-1 overflow-hidden overflow-x-auto">
				<table className="w-full min-w-[700px]">
					<thead>
						<tr className="text-left">
							<th>Personal Trainer</th>
							<th>Pacote</th>
							<th>Licenças</th>
							<th>Valor pago</th>
							<th>Data</th>
						</tr>
					</thead>
					<tbody>
						{purchases.map((p) => (
							<tr key={p.id} className="even:bg-gpa-blue-50">
								<td className="py-2">
									<p className="font-medium">{p.personalTrainer.name}</p>
									<p className="text-sm text-slate-500">
										{p.personalTrainer.documentNumber}
									</p>
								</td>
								<td className="py-2">{p.licenseExtension.name}</td>
								<td className="py-2">{p.licensesAmount}</td>
								<td className="py-2 font-semibold">
									{numberToBRL(p.proratedPrice / 100)}
								</td>

								<td className="py-2">{getLocalDate(p.createdAt)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="self-end">
				<PageNavigation
					numPages={Math.ceil(totalItems / filterQuery.size)}
					currentPage={filterQuery.page}
					onPageChange={handleUpdatePage}
				/>
			</div>
		</div>
	);
};
